import { EMOJI_NAME_PATTERN, MESSAGING_LIMITS } from '../enums';
import {
  type BlockNode,
  CODE_LANGUAGE_PATTERN,
  type InlineNode,
  LINK_HREF_PATTERN,
  type ListItemNode,
  MAX_LINK_HREF_LENGTH,
  MENTION_NODE_KINDS,
  type MentionNodeKind,
  type RichTextDoc,
  type RichTextMark,
  type RichTextMarkType,
  type TextNode,
} from './schema';

/**
 * Server-side validation of message bodies (D-045, PLAN §6 Phase 1 risk "rich-text XSS or
 * oversized documents"). `validateRichText()` walks the document once and either returns a
 * normalised copy — only whitelisted keys, text in NFC, marks de-duplicated, TipTap presentation
 * attrs dropped — or the first problem with its path (`content[0].content[2].marks[0]`). The API
 * answers `422 invalid_rich_text` with `details = { reason, path }`, or `422 message_too_long`
 * for `too_large`.
 */

export interface RichTextLimits {
  /** UTF-8 bytes of the JSON document. Default 256 KiB. */
  maxBytes: number;
  /** Block nesting depth. Default 16. */
  maxDepth: number;
  /** Nodes in the document. Default 10,000. */
  maxNodes: number;
}

export const DEFAULT_RICHTEXT_LIMITS: RichTextLimits = Object.freeze({
  maxBytes: MESSAGING_LIMITS.maxContentBytes,
  maxDepth: MESSAGING_LIMITS.maxContentDepth,
  maxNodes: MESSAGING_LIMITS.maxContentNodes,
});

export type RichTextInvalidReason =
  | 'not_a_document'
  | 'unknown_node'
  | 'unknown_mark'
  | 'unknown_attr'
  | 'invalid_content'
  | 'invalid_attrs'
  | 'invalid_text'
  | 'too_deep'
  | 'too_many_nodes'
  | 'too_large';

export type RichTextValidation =
  | { ok: true; doc: RichTextDoc; bytes: number }
  | { ok: false; reason: RichTextInvalidReason; path: string; message: string };

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Control characters other than tab (and newline, which only code blocks may contain). */
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const CONTROL_EXCEPT_TAB = /[\u0000-\u0008\u000A-\u001F\u007F-\u009F]/;
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const CONTROL_EXCEPT_TAB_NEWLINE = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F]/;
const MAX_LABEL_LENGTH = 200;

/** UTF-8 byte length of a string (no TextEncoder: this package runs without DOM or Node types). */
export function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < value.length) {
      const next = value.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        i++;
      } else bytes += 3;
    } else bytes += 3;
  }
  return bytes;
}

class Invalid extends Error {
  constructor(
    readonly reason: RichTextInvalidReason,
    readonly path: string,
    message: string,
  ) {
    super(message);
  }
}

type Json = Record<string, unknown>;

function isObject(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function checkKeys(node: Json, allowed: readonly string[], path: string): void {
  for (const key of Object.keys(node)) {
    if (!allowed.includes(key)) {
      throw new Invalid('unknown_attr', `${path}.${key}`, `Unexpected key "${key}"`);
    }
  }
}

function checkAttrKeys(
  attrs: Json,
  allowed: readonly string[],
  ignored: readonly string[],
  path: string,
): void {
  for (const key of Object.keys(attrs)) {
    if (!allowed.includes(key) && !ignored.includes(key)) {
      throw new Invalid('unknown_attr', `${path}.attrs.${key}`, `Unexpected attribute "${key}"`);
    }
  }
}

function optionalLabel(value: unknown, path: string): string | null | undefined {
  if (value === undefined || value === null) return value;
  if (typeof value !== 'string' || value.length > MAX_LABEL_LENGTH) {
    throw new Invalid('invalid_attrs', `${path}.attrs.label`, 'label must be a short string');
  }
  return value.normalize('NFC');
}

class Walker {
  nodes = 0;
  constructor(readonly limits: RichTextLimits) {}

  count(path: string): void {
    this.nodes += 1;
    if (this.nodes > this.limits.maxNodes) {
      throw new Invalid('too_many_nodes', path, `More than ${this.limits.maxNodes} nodes`);
    }
  }

  doc(input: unknown): RichTextDoc {
    if (!isObject(input) || input.type !== 'doc') {
      throw new Invalid('not_a_document', '', 'The body must be a document of type "doc"');
    }
    checkKeys(input, ['type', 'content'], '');
    const content = this.blocks(input.content, 'content', 1, { allowEmpty: false });
    return { type: 'doc', content };
  }

  blocks(
    input: unknown,
    path: string,
    depth: number,
    options: { allowEmpty: boolean },
  ): BlockNode[] {
    if (depth > this.limits.maxDepth) {
      throw new Invalid('too_deep', path, `Nested more than ${this.limits.maxDepth} levels`);
    }
    if (!Array.isArray(input) || (!options.allowEmpty && input.length === 0)) {
      throw new Invalid('invalid_content', path, 'Expected a non-empty list of blocks');
    }
    return input.map((child, index) => this.block(child, `${path}[${index}]`, depth));
  }

  block(input: unknown, path: string, depth: number): BlockNode {
    if (!isObject(input) || typeof input.type !== 'string') {
      throw new Invalid('invalid_content', path, 'Expected a node');
    }
    this.count(path);
    switch (input.type) {
      case 'paragraph': {
        checkKeys(input, ['type', 'content', 'attrs'], path);
        this.noAttrs(input, path);
        const content = this.inline(input.content, `${path}.content`);
        return content.length > 0 ? { type: 'paragraph', content } : { type: 'paragraph' };
      }
      case 'codeBlock': {
        checkKeys(input, ['type', 'content', 'attrs'], path);
        let language: string | null = null;
        if (input.attrs !== undefined && input.attrs !== null) {
          if (!isObject(input.attrs)) {
            throw new Invalid('invalid_attrs', `${path}.attrs`, 'attrs must be an object');
          }
          checkAttrKeys(input.attrs, ['language'], [], path);
          const value = input.attrs.language;
          if (value !== undefined && value !== null) {
            if (typeof value !== 'string' || !CODE_LANGUAGE_PATTERN.test(value)) {
              throw new Invalid('invalid_attrs', `${path}.attrs.language`, 'Unknown language');
            }
            language = value;
          }
        }
        const content = this.codeText(input.content, `${path}.content`);
        return content.length > 0
          ? { type: 'codeBlock', attrs: { language }, content }
          : { type: 'codeBlock', attrs: { language } };
      }
      case 'blockquote': {
        checkKeys(input, ['type', 'content', 'attrs'], path);
        this.noAttrs(input, path);
        return {
          type: 'blockquote',
          content: this.blocks(input.content, `${path}.content`, depth + 1, { allowEmpty: false }),
        };
      }
      case 'bulletList': {
        checkKeys(input, ['type', 'content', 'attrs'], path);
        this.noAttrs(input, path);
        return { type: 'bulletList', content: this.items(input.content, path, depth) };
      }
      case 'orderedList': {
        checkKeys(input, ['type', 'content', 'attrs'], path);
        let start = 1;
        if (input.attrs !== undefined && input.attrs !== null) {
          if (!isObject(input.attrs)) {
            throw new Invalid('invalid_attrs', `${path}.attrs`, 'attrs must be an object');
          }
          checkAttrKeys(input.attrs, ['start'], ['type'], path);
          const value = input.attrs.start;
          if (value !== undefined && value !== null) {
            if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 1e6) {
              throw new Invalid('invalid_attrs', `${path}.attrs.start`, 'start must be an integer');
            }
            start = value;
          }
        }
        return {
          type: 'orderedList',
          attrs: { start },
          content: this.items(input.content, path, depth),
        };
      }
      default:
        throw new Invalid(
          RICHTEXT_KNOWN_INLINE.has(input.type) ? 'invalid_content' : 'unknown_node',
          `${path}.type`,
          `"${input.type}" is not allowed here`,
        );
    }
  }

  items(input: unknown, path: string, depth: number): ListItemNode[] {
    const itemsPath = `${path}.content`;
    if (!Array.isArray(input) || input.length === 0) {
      throw new Invalid('invalid_content', itemsPath, 'A list needs at least one item');
    }
    return input.map((child, index) => {
      const itemPath = `${itemsPath}[${index}]`;
      if (!isObject(child) || child.type !== 'listItem') {
        throw new Invalid('invalid_content', itemPath, 'Lists contain only listItem nodes');
      }
      this.count(itemPath);
      checkKeys(child, ['type', 'content', 'attrs'], itemPath);
      this.noAttrs(child, itemPath);
      const content = this.blocks(child.content, `${itemPath}.content`, depth + 1, {
        allowEmpty: false,
      });
      if (content[0]?.type !== 'paragraph') {
        throw new Invalid(
          'invalid_content',
          `${itemPath}.content[0]`,
          'A list item starts with a paragraph',
        );
      }
      return { type: 'listItem', content };
    });
  }

  noAttrs(node: Json, path: string): void {
    if (node.attrs === undefined || node.attrs === null) return;
    if (!isObject(node.attrs) || Object.keys(node.attrs).length > 0) {
      throw new Invalid('unknown_attr', `${path}.attrs`, 'This node takes no attributes');
    }
  }

  codeText(input: unknown, path: string): TextNode[] {
    if (input === undefined || input === null) return [];
    if (!Array.isArray(input)) throw new Invalid('invalid_content', path, 'Expected a list');
    const result: TextNode[] = [];
    input.forEach((child, index) => {
      const childPath = `${path}[${index}]`;
      if (!isObject(child) || child.type !== 'text') {
        throw new Invalid('invalid_content', childPath, 'Code blocks contain only text');
      }
      this.count(childPath);
      checkKeys(child, ['type', 'text', 'marks'], childPath);
      if (Array.isArray(child.marks) && child.marks.length > 0) {
        throw new Invalid(
          'invalid_content',
          `${childPath}.marks`,
          'Code blocks cannot be formatted',
        );
      }
      const text = this.text(child.text, `${childPath}.text`, true);
      if (text) result.push({ type: 'text', text });
    });
    return result;
  }

  text(value: unknown, path: string, allowNewlines: boolean): string {
    if (typeof value !== 'string') throw new Invalid('invalid_text', path, 'text must be a string');
    const pattern = allowNewlines ? CONTROL_EXCEPT_TAB_NEWLINE : CONTROL_EXCEPT_TAB;
    if (pattern.test(value)) {
      throw new Invalid('invalid_text', path, 'Control characters are not allowed');
    }
    return value.normalize('NFC');
  }

  inline(input: unknown, path: string): InlineNode[] {
    if (input === undefined || input === null) return [];
    if (!Array.isArray(input)) throw new Invalid('invalid_content', path, 'Expected a list');
    const result: InlineNode[] = [];
    input.forEach((child, index) => {
      const node = this.inlineNode(child, `${path}[${index}]`);
      if (node) result.push(node);
    });
    return result;
  }

  inlineNode(input: unknown, path: string): InlineNode | null {
    if (!isObject(input) || typeof input.type !== 'string') {
      throw new Invalid('invalid_content', path, 'Expected a node');
    }
    this.count(path);
    switch (input.type) {
      case 'text': {
        checkKeys(input, ['type', 'text', 'marks'], path);
        const text = this.text(input.text, `${path}.text`, false);
        if (!text) return null; // ProseMirror has no empty text nodes.
        const marks = this.marks(input.marks, `${path}.marks`);
        return marks.length > 0 ? { type: 'text', text, marks } : { type: 'text', text };
      }
      case 'hardBreak':
        checkKeys(input, ['type', 'attrs', 'marks'], path);
        return { type: 'hardBreak' };
      case 'mention': {
        checkKeys(input, ['type', 'attrs', 'marks'], path);
        const attrs = this.attrs(input, path);
        checkAttrKeys(attrs, ['kind', 'id', 'label'], ['mentionSuggestionChar'], path);
        const kind = attrs.kind;
        if (typeof kind !== 'string' || !(MENTION_NODE_KINDS as readonly string[]).includes(kind)) {
          throw new Invalid('invalid_attrs', `${path}.attrs.kind`, 'Unknown mention kind');
        }
        const id = attrs.id;
        const broadcast = kind === 'channel' || kind === 'here';
        if (typeof id !== 'string' || (broadcast ? id !== kind : !UUID_PATTERN.test(id))) {
          throw new Invalid('invalid_attrs', `${path}.attrs.id`, 'Invalid mention id');
        }
        const label = optionalLabel(attrs.label, path);
        return {
          type: 'mention',
          attrs: {
            kind: kind as MentionNodeKind,
            id: broadcast ? id : id.toLowerCase(),
            ...(label !== undefined ? { label } : {}),
          },
        };
      }
      case 'channelLink': {
        checkKeys(input, ['type', 'attrs', 'marks'], path);
        const attrs = this.attrs(input, path);
        checkAttrKeys(attrs, ['id', 'label'], [], path);
        if (typeof attrs.id !== 'string' || !UUID_PATTERN.test(attrs.id)) {
          throw new Invalid('invalid_attrs', `${path}.attrs.id`, 'Invalid channel id');
        }
        const label = optionalLabel(attrs.label, path);
        return {
          type: 'channelLink',
          attrs: { id: attrs.id.toLowerCase(), ...(label !== undefined ? { label } : {}) },
        };
      }
      case 'emoji': {
        checkKeys(input, ['type', 'attrs', 'marks'], path);
        const attrs = this.attrs(input, path);
        checkAttrKeys(attrs, ['name'], [], path);
        if (typeof attrs.name !== 'string' || !EMOJI_NAME_PATTERN.test(attrs.name)) {
          throw new Invalid('invalid_attrs', `${path}.attrs.name`, 'Invalid emoji name');
        }
        return { type: 'emoji', attrs: { name: attrs.name } };
      }
      default:
        throw new Invalid(
          RICHTEXT_KNOWN_BLOCK.has(input.type) ? 'invalid_content' : 'unknown_node',
          `${path}.type`,
          `"${input.type}" is not allowed here`,
        );
    }
  }

  attrs(node: Json, path: string): Json {
    if (!isObject(node.attrs))
      throw new Invalid('invalid_attrs', `${path}.attrs`, 'attrs required');
    return node.attrs;
  }

  marks(input: unknown, path: string): RichTextMark[] {
    if (input === undefined || input === null) return [];
    if (!Array.isArray(input)) throw new Invalid('invalid_content', path, 'marks must be a list');
    const seen = new Set<RichTextMarkType>();
    const result: RichTextMark[] = [];
    input.forEach((mark, index) => {
      const markPath = `${path}[${index}]`;
      if (!isObject(mark) || typeof mark.type !== 'string') {
        throw new Invalid('invalid_content', markPath, 'Expected a mark');
      }
      checkKeys(mark, ['type', 'attrs'], markPath);
      const type = mark.type;
      if (type === 'link') {
        const attrs = this.attrs(mark, markPath);
        checkAttrKeys(attrs, ['href'], ['target', 'rel', 'class'], markPath);
        const href = attrs.href;
        if (
          typeof href !== 'string' ||
          href.length > MAX_LINK_HREF_LENGTH ||
          !LINK_HREF_PATTERN.test(href)
        ) {
          throw new Invalid(
            'invalid_attrs',
            `${markPath}.attrs.href`,
            'Links must be http, https or mailto',
          );
        }
        if (!seen.has('link')) result.push({ type: 'link', attrs: { href } });
        seen.add('link');
        return;
      }
      if (type !== 'bold' && type !== 'italic' && type !== 'strike' && type !== 'code') {
        throw new Invalid('unknown_mark', `${markPath}.type`, `Mark "${type}" is not allowed`);
      }
      if (mark.attrs !== undefined && mark.attrs !== null) {
        if (!isObject(mark.attrs) || Object.keys(mark.attrs).length > 0) {
          throw new Invalid('unknown_attr', `${markPath}.attrs`, 'This mark takes no attributes');
        }
      }
      if (!seen.has(type)) result.push({ type });
      seen.add(type);
    });
    return result;
  }
}

const RICHTEXT_KNOWN_INLINE = new Set(['text', 'hardBreak', 'mention', 'channelLink', 'emoji']);
const RICHTEXT_KNOWN_BLOCK = new Set([
  'paragraph',
  'blockquote',
  'codeBlock',
  'bulletList',
  'orderedList',
  'listItem',
]);

/** Validates and normalises a message body against the shared schema. Never throws. */
export function validateRichText(
  input: unknown,
  limits: Partial<RichTextLimits> = {},
): RichTextValidation {
  const effective: RichTextLimits = { ...DEFAULT_RICHTEXT_LIMITS, ...limits };
  let serialised: string;
  try {
    serialised = JSON.stringify(input) ?? '';
  } catch {
    return { ok: false, reason: 'not_a_document', path: '', message: 'Not JSON-serialisable' };
  }
  const bytes = utf8ByteLength(serialised);
  if (bytes > effective.maxBytes) {
    return {
      ok: false,
      reason: 'too_large',
      path: '',
      message: `The document is larger than ${effective.maxBytes} bytes`,
    };
  }
  try {
    const doc = new Walker(effective).doc(input);
    return { ok: true, doc, bytes: utf8ByteLength(JSON.stringify(doc)) };
  } catch (error) {
    if (error instanceof Invalid) {
      return { ok: false, reason: error.reason, path: error.path, message: error.message };
    }
    throw error;
  }
}

/** Type guard over {@link validateRichText}. */
export function isValidRichText(input: unknown): input is RichTextDoc {
  return validateRichText(input).ok;
}

/**
 * True when the document has nothing to send: only whitespace text and line breaks (an empty
 * composer, or Enter pressed on blank lines). Mentions, channel links and emoji count as content.
 */
export function isEmptyDoc(doc: RichTextDoc): boolean {
  const blockEmpty = (block: BlockNode): boolean => {
    switch (block.type) {
      case 'paragraph':
        return (block.content ?? []).every(
          (node) => node.type === 'hardBreak' || (node.type === 'text' && node.text.trim() === ''),
        );
      case 'codeBlock':
        return (block.content ?? []).every((node) => node.text.trim() === '');
      case 'blockquote':
        return block.content.every(blockEmpty);
      case 'bulletList':
      case 'orderedList':
        return block.content.every((item) => item.content.every(blockEmpty));
    }
  };
  return doc.content.every(blockEmpty);
}
